import React from 'react';
import WeekModalDay from './components/WeekModalDay/WeekModalDay';
import WeekModalCalendar from './containers/WeekModalCalendar/WeekModalCalendar';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { IWeekModalSaveParams, IWeekModalTime } from 'src/mobx/modalStore/types';
import { TBooleanInNumber } from 'src/types/main';
import { booleanToNumber } from 'src/utils/helpers';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalFooter from 'src/view/components/Controls/Modal/ModalFooter/ModalFooter';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import TimePicker from 'src/view/components/Controls/Pickers/TimePicker/TimePicker';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { DAYS_OF_WEEK, TimePickerMode, defaultTime, fullWeek } from '~/constants/dateTime';
import './style.scss';

export default class WeekModal extends React.Component<{}, IWeekModalSaveParams> {
  state = {
    startDate: modalStore.weekModal?.startDate,
    endDate: modalStore.weekModal?.endDate,
    startTime: modalStore.weekModal?.startTime || defaultTime,
    endTime: modalStore.weekModal?.endTime || defaultTime,
    allDay: modalStore.weekModal?.allDay,
    hideAdditionalSettings: modalStore.weekModal?.hideAdditionalSettings || false,
    timePickerMode: modalStore.weekModal?.timePickerMode || TimePickerMode.GENERAL,
    daysOfWeek: modalStore.weekModal?.daysOfWeek || [],
    timeByDays: modalStore.weekModal?.timeByDays,
    messageOnError: '',
  };

  closeModal = () => {
    modalStore.closeWeekModal();
  };

  clickSave = () => {
    const isValidForm = Array.from(this.state.timeByDays?.values() || []).every((item) => item.isValid);

    if (!isValidForm) {
      this.setState({ messageOnError: modalStore.weekModal?.messageOnError });
      return;
    }

    modalStore.weekModal?.onSave({
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      startTime: this.state.startTime,
      endTime: this.state.allDay ? undefined : this.state.endTime,
      allDay: this.state.allDay,
      daysOfWeek: this.state.daysOfWeek,
      timeByDays: this.state.timeByDays,
    });
  };

  changeDate = (date: { start_date: Date | undefined; end_date: Date | undefined }) => {
    this.setState({ startDate: date.start_date, endDate: date.end_date && isNaN(date.end_date?.valueOf()) ? date.start_date : date.end_date });
  };

  changeAllDay = (status: TBooleanInNumber) => {
    if (status) {
      this.setState({ allDay: Boolean(status), startTime: undefined, endTime: undefined });
    } else {
      this.setState({ allDay: Boolean(status), startTime: this.state.startTime, endTime: this.state.endTime });
    }
  };

  changeDaysOfWeek = (day: DAYS_OF_WEEK) => {
    const alreadySelect: boolean = this.state.daysOfWeek.includes(day);

    // if only one day is selected - do not let remove choice from it
    if (modalStore.weekModal?.minimumOneDayInWeek && this.state.daysOfWeek.length === 1 && alreadySelect) return;

    this.setState({
      daysOfWeek: alreadySelect ? this.state.daysOfWeek.filter((item) => item !== day) : this.state.daysOfWeek.concat(day),
    });
  };

  changeTime = (data: { time: Date; type: 'start' | 'end' }) => {
    if (data.type === 'start') {
      this.setState({ startTime: data.time });
    } else {
      this.setState({ endTime: data.time });
    }
  };

  changeTimeByDay = (day: DAYS_OF_WEEK, time: Date, type: 'start' | 'end') => {
    const timeByDays = new Map(this.state.timeByDays);
    const currDayTime = (timeByDays.get(day) || {}) as IWeekModalTime;

    if (type === 'start') {
      timeByDays.set(day, {
        ...currDayTime,
        startTime: time,
        isValid: modalStore.weekModal?.validateTime?.(time, type, currDayTime?.endTime) ?? true,
      });
    } else {
      timeByDays.set(day, {
        ...currDayTime,
        endTime: time,
        isValid: modalStore.weekModal?.validateTime?.(time, type, currDayTime?.startTime) ?? true,
      });
    }

    this.setState({ timeByDays });
  };

  render() {
    return (
      <CustomModalComponent
        className="week-modal"
        modalMaxWidth={this.state.timePickerMode === TimePickerMode.DAYTIME_RANGE ? 1150 : 900}
        isOpen
        onRequestClose={this.closeModal}
        withCloseIcon
      >
        <ModalBackground>
          <ModalHeader title={localeStore.t(modalStore.weekModal?.title || 'validity')} />
          <ScrollableBlock showScrollbar className="d-flex flex-1 mr-1" scrollClassName="d-flex flex-row px-8 pt-6 full-height">
            <WeekModalCalendar start_date={this.state.startDate} end_date={this.state.endDate} changeDate={this.changeDate} />
            {!modalStore.weekModal?.hideAdditionalSettings && (
              <>
                <div className="d-flex flex-column ml-6">
                  {this.state.allDay !== undefined ? (
                    <div className="d-flex flex-row mb-6">
                      <Switcher status={booleanToNumber(this.state.allDay)} onChange={this.changeAllDay} />
                      <p className="family-medium text-size-18 color-dark ml-4">{localeStore.t('all_day')}</p>
                    </div>
                  ) : null}
                  <div className="d-flex flex-column">
                    {fullWeek.map((day, idx) => (
                      <WeekModalDay
                        key={idx}
                        day={day}
                        index={idx}
                        startTime={this.state.timeByDays?.get(day)?.startTime}
                        endTime={this.state.timeByDays?.get(day)?.endTime}
                        error={this.state.timeByDays?.get(day)?.isValid === false}
                        active={this.state.daysOfWeek.includes(day)}
                        timePickerMode={this.state.timePickerMode}
                        changeDay={this.changeDaysOfWeek}
                        changeTime={this.changeTimeByDay}
                        disabled={this.state.allDay}
                      />
                    ))}
                    {this.state.messageOnError ? <p className="color-red mt-3 text-size-14">{localeStore.t(this.state.messageOnError)}</p> : null}
                  </div>
                </div>
                {this.state.timePickerMode === TimePickerMode.GENERAL ? (
                  <div className="d-flex flex-column ml-6">
                    <div className="d-flex flex-row flex-wrap align-items-center mb-4">
                      <p className="week-modal__time-title family-regular text-size-18 color-gray2 text-uppercase letter-spacing-12">
                        {localeStore.t('txt_from')}
                      </p>
                      <TimePicker
                        date={this.state.startTime}
                        onTimeChanged={(time) => this.changeTime({ time, type: 'start' })}
                        disabled={this.state.allDay}
                      />
                    </div>
                    <div className="d-flex flex-row flex-wrap align-items-center">
                      <p className="week-modal__time-title family-regular text-size-18 color-gray2 text-uppercase letter-spacing-12">
                        {localeStore.t('txt_select_to')}
                      </p>
                      <TimePicker
                        date={this.state.endTime}
                        onTimeChanged={(time) => this.changeTime({ time, type: 'end' })}
                        disabled={this.state.allDay}
                      />
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </ScrollableBlock>

          <ModalFooter>
            <Button title={localeStore.t('txt_save')} onClick={this.clickSave} size="large" />
          </ModalFooter>
        </ModalBackground>
      </CustomModalComponent>
    );
  }
}
